import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import SimpleTable from "@components/simpleTable/SimpleTable";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { fetchSelectItems } from "@components/smart/smartSelect/SmartSelectAPI";
import { IColumn, IRow } from "@components/table";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import { IAssetEntity } from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { reloadAccounts } from "@pages/accountAssignment/Account.utils";
import {
    _getLowerTaxAccountLocalPath,
    ILowerPriceDialogEntity,
    isApplyGrantType,
    isLowerPriceType,
    IUnappliedGrantItem,
    LowerPriceAmountPath,
    LowerPriceCreditAccountPath,
    LowerPriceDatePath,
    LowerPriceDebitAccountPath,
    lowerTaxAssetPrice,
    unappliedGrantItems
} from "@pages/asset/fixedAsset/LowerTaxPrice.utils";
import { getFiscalDataCorrespondingToDateAccountingTransaction } from "@pages/documents/Document.utils";
import { isNonProfitCompany } from "@utils/CompanyUtils";
import { memoizeOne } from "@utils/memoizeOne";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { DASH_CHARACTER } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { RowType, TextAlign } from "../../../enums";
import BindingContext from "../../../odata/BindingContext";
import { formatCurrency } from "../../../types/Currency";
import DialogFormView, { IDialogFormViewProps } from "../../../views/formView/DialogFormView";
import { IFormStorageSaveResult, IGetCorrectErrorBc } from "../../../views/formView/FormStorage";
import { TableWrapper } from "../../../views/table/TableView.styles";
import { ILowerPriceCustomData } from "./FixedAsset.utils";

interface IProps extends IDialogFormViewProps<ILowerPriceDialogEntity, ILowerPriceCustomData> {

}

interface IState {
    areTableRowsExpanded: boolean;
}

class LowerTaxPriceDialogFormView extends DialogFormView<ILowerPriceDialogEntity, IProps, IState> {
    static contextType = AppContext;
    _items: IUnappliedGrantItem[];
    _lastDate: Date;

    get hasGrantAccounts(): boolean {
        return isNonProfitCompany(this.context);
    }

    onAfterLoad = async () => {
        const { storage } = this.props;
        const { assetStorage } = storage.getCustomData();

        if (isApplyGrantType({ storage })) {
            this._items = cloneDeep(unappliedGrantItems(assetStorage));
            let sum = 0;
            this._items.forEach(item => {
                sum += item.UnappliedGrantAmount;
            });
            storage.setValueByPath(LowerPriceAmountPath, sum);
        }

        if (this.hasGrantAccounts) {
            const { GrantEquityAccount, GrantOtherIncomeAccount, IsImportedAsset } = assetStorage.data.origEntity as IAssetEntity;
            if (GrantEquityAccount?.Id || GrantOtherIncomeAccount?.Id) {
                storage.setValueByPath(LowerPriceDebitAccountPath, GrantEquityAccount?.Id);
                storage.setValueByPath(_getLowerTaxAccountLocalPath("Debit", "Number"), GrantEquityAccount?.Number);
                storage.setValueByPath(_getLowerTaxAccountLocalPath("Debit", "Name"), GrantEquityAccount?.Name);

                storage.setValueByPath(LowerPriceCreditAccountPath, GrantOtherIncomeAccount?.Id);
                storage.setValueByPath(_getLowerTaxAccountLocalPath("Credit", "Number"), GrantOtherIncomeAccount?.Number);
                storage.setValueByPath(_getLowerTaxAccountLocalPath("Credit", "Name"), GrantOtherIncomeAccount?.Name);
            }
            storage.setCustomData({ DisableAccounts: GrantEquityAccount?.Id && GrantOtherIncomeAccount?.Id && IsImportedAsset });

            this._lastDate = storage.getValueByPath(LowerPriceDatePath) as Date;
            await this.initAccounts();
        }

        storage.refresh(true);

        return super.onAfterLoad();
    };

    shouldComponentUpdate(nextProps: Readonly<IProps>, nextState: Readonly<IState>): boolean {
        return super.shouldComponentUpdate(nextProps, nextState)
            || this.state?.areTableRowsExpanded !== nextState?.areTableRowsExpanded;
    }

    getCorrespondingFiscalData() {
        const { storage } = this.props;
        const currentDate = storage.getValueByPath(LowerPriceDatePath);
        const previousFiscalData = getFiscalDataCorrespondingToDateAccountingTransaction(storage, this._lastDate);
        const { fiscalYear } = getFiscalDataCorrespondingToDateAccountingTransaction(storage, currentDate);
        const hasChanged = previousFiscalData.fiscalYear?.Id !== fiscalYear?.Id;

        this._lastDate = currentDate;
        return { hasChanged, fiscalYear };
    }

    handleChange(e: ISmartFieldChange): void {
        super.handleChange(e);

        this.handleDecisiveDateChange(e);
    }

    async handleDecisiveDateChange(e: ISmartFieldChange): Promise<void> {
        const path = e.bindingContext.getPath();
        if (path === LowerPriceDatePath && this.hasGrantAccounts) {
            const { storage } = this.props;
            const { hasChanged, fiscalYear } = this.getCorrespondingFiscalData();

            if (hasChanged) {
                // we can't let user save the form before the values are changed => busy indicator
                storage.setBusy(true);
                await Promise.all([
                    reloadAccounts(storage, !!fiscalYear, LowerPriceDebitAccountPath, _getLowerTaxAccountLocalPath("Debit", "Number")),
                    reloadAccounts(storage, !!fiscalYear, LowerPriceCreditAccountPath, _getLowerTaxAccountLocalPath("Credit", "Number")),
                ]);
                storage.setBusy(false);
                storage.refresh(true);
            }
        }
    }

    initAccounts = (): Promise<void[]> => {
        const { storage } = this.props;
        const promises = [LowerPriceDebitAccountPath, LowerPriceCreditAccountPath].map(async (path) => {
            const bc = storage.data.bindingContext.navigate(path);
            const fieldInfo = storage.getInfo(bc);
            const items = await fetchSelectItems({
                fieldInfo,
                oData: storage.oData,
                appContext: this.context,
                storage
            });
            fieldInfo.fieldSettings.items = items;
            if (items?.length === 1) {
                const firstItem = items?.[0];
                storage.clearAndSetValue(bc, firstItem.id ?? null);
                storage.processDependentField(fieldInfo.fieldSettings.localDependentFields, firstItem.additionalData, bc);
            }
        });
        return Promise.all(promises);
    }

    handleGroupToggle = () => {
        this.setState((prevState) => ({
            areTableRowsExpanded: !prevState?.areTableRowsExpanded
        }));
    }

    getRows = memoizeOne(() => {
        const { storage } = this.props;
        const { context } = storage;

        const data = this._items ?? [];
        const childRows: IRow[] = data.map(item => ({
            id: item.Id,
            values: {
                Document: item.Document?.Id ? getTableIntentLink(item.Document.NumberOurs, {
                    route: `${getRouteByDocumentType(item.Document.DocumentTypeCode as DocumentTypeCode)}/${item.Document.Id}`,
                    context, storage
                }) : DASH_CHARACTER,
                Grant: item.ShortGrantName,
                Amount: formatCurrency(item.UnappliedGrantAmount, "CZK")
            }
        }));

        const GROUP_ID = "grant-rows";

        return childRows?.length < 2 ? childRows : [{
            id: GROUP_ID,
            values: { Document: storage.t("FixedAsset:LowerTaxPrice.Items", { count: data.length }) },
            type: RowType.Group,
            rows: childRows,
            open: !!this.state?.areTableRowsExpanded
        }];
    }, () => [this.state?.areTableRowsExpanded, this._items]);

    getColumns = memoizeOne(() => {
        const { storage } = this.props;
        return [
            { id: "Document", label: `${storage.t("FixedAsset:LowerTaxPrice.Document")}:` },
            { id: "Grant", label: `${storage.t("FixedAsset:LowerTaxPrice.Grant")}:` },
            { id: "Amount", label: `${storage.t("FixedAsset:LowerTaxPrice.GrantAmount")}:`, textAlign: TextAlign.Right },
        ] as IColumn[];
    });

    renderCustomHeader(): ReactElement | null {
        if (isLowerPriceType({ storage : this.props.storage })) {
            return null;
        }

        // render table with collapsible rows showing bounded grants
        return (
            <TableWrapper>
                <SimpleTable onGroupToggle={this.handleGroupToggle}
                             rows={this.getRows()} columns={this.getColumns()}
                             style={{ marginBottom: "20px" }} />
            </TableWrapper>
        );
    }

    getCorrectErrorBc = (args: IGetCorrectErrorBc): BindingContext => {
        const path = args.matchedBindingContext.getPath();
        const { bindingContext } = this.props.storage.data;
        if (path === BindingContext.localContext("Date")) {
            // BE validates Date field, but local context
            return bindingContext.navigate(LowerPriceDatePath);
        }

        return args.matchedBindingContext;
    };

    save = async (): Promise<IFormStorageSaveResult> => {
        const { storage } = this.props;
        if (await storage.validate(true)) {
            this.forceUpdate();
            return null;
        }

        const { bindingContext, entity, origEntity } = storage.data;

        storage.setBusy();
        this.forceUpdate();

        try {
            await lowerTaxAssetPrice(storage, origEntity.AssetId as number);
        } catch (error) {
            await storage.handleOdataError({
                error,
                bindingContext,
                getCorrectErrorBc: this.getCorrectErrorBc,
                fieldValidationWithoutErrorAlert: true
            });
            storage.setBusy(false);
            this.forceUpdate();
            return null;
        }

        return {
            bindingContext,
            data: entity
        };
    };
}

export default withPermissionContext(LowerTaxPriceDialogFormView);